import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import UserAvatar from './UserAvatar';
import { DemandDetailModal } from './DemandDetailModal';
import ContactDetailModalMobile from './ContactDetailModalMobile';
import type {
  CityModalProps,
  CityElectionResult,
  CityModalDemanda,
} from './CityModal';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TARGET_YEAR = 2026;

const fmtN = (n: number | null | undefined) =>
  n != null ? n.toLocaleString('pt-BR') : '—';

const fmtD = (d: string | null | undefined) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const ago = (d: string | null | undefined) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return `${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  const days = Math.floor(diff / 86400);
  if (days === 1) return 'ontem';
  if (days < 7) return `${days}d`;
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
};

const STATUS_LABEL: Record<string, string> = {
  NAO_INICIADA: 'Não Iniciada',
  EM_ANDAMENTO: 'Em Andamento',
  PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

// ─── Animations ───────────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`;
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;

const shimmerAnim = keyframes`
  0%   { background-position: -400px 0; }
  100% { background-position:  400px 0; }
`;
const shimmerBg = `
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 800px 100%;
`;
const MShimmerRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 10px 0; border-bottom: 1px solid #f1f5f9;
`;
const MShimmerCircle = styled.div`
  width: 36px; height: 36px; border-radius: 50%; flex-shrink: 0;
  ${shimmerBg}
  animation: ${shimmerAnim} 1.4s infinite linear;
`;
const MShimmerBar = styled.div<{ $w?: string; $h?: string }>`
  width: ${p => p.$w ?? '120px'}; height: ${p => p.$h ?? '13px'}; border-radius: 6px;
  ${shimmerBg}
  animation: ${shimmerAnim} 1.4s infinite linear;
`;

// ─── Overlay & Sheet ──────────────────────────────────────────────────────────

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 900px) {
    display: contents;
  }
`;

const Overlay = styled.div<{ $open: boolean }>`
  display: none;
  @media (max-width: 900px) {
    display: flex;
    position: fixed;
    inset: 0;
    z-index: 3000;
    flex-direction: column;
    opacity: ${p => (p.$open ? 1 : 0)};
    pointer-events: ${p => (p.$open ? 'auto' : 'none')};
    transition: opacity 0.24s ease;
  }
`;

const Sheet = styled.div<{ $open: boolean }>`
  background: #f8fafc;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transform: ${p => (p.$open ? 'translateY(0)' : 'translateY(100%)')};
  transition: transform 0.32s cubic-bezier(0.2, 0.8, 0.2, 1);
`;

// ─── Nav ──────────────────────────────────────────────────────────────────────

const Nav = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 12px 8px 10px;
  background: rgba(248, 250, 252, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const NavIconBtn = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(120, 120, 128, 0.12);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #007aff;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: background 0.12s;
  &:active { background: rgba(120, 120, 128, 0.24); }
`;

const NavCenter = styled.div`
  flex: 1;
  text-align: center;
  padding: 0 6px;
  overflow: hidden;
`;

const NavSup = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1;
`;

const NavTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #000;
  letter-spacing: -0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// ─── Scrollable body ──────────────────────────────────────────────────────────

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 8px;
`;

// ─── Hero ─────────────────────────────────────────────────────────────────────

const Hero = styled.div`
  padding: 20px 20px 14px;
`;

const HeroName = styled.h1`
  font-size: 30px;
  font-weight: 700;
  color: #000;
  letter-spacing: -0.03em;
  margin: 0 0 5px;
  line-height: 1.1;
`;

const HeroSub = styled.div`
  font-size: 14px;
  color: #8e8e93;
  line-height: 1.5;
`;

// ─── Section ──────────────────────────────────────────────────────────────────

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 8px;
`;

const SectionLabel = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #6c6c70;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SectionEditBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 13px;
  color: #007aff;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
`;

// ─── Generic card ─────────────────────────────────────────────────────────────

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  margin: 0 16px;
  overflow: hidden;
`;

// ─── Sobre a cidade rows ──────────────────────────────────────────────────────

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 16px;
  border-bottom: 1px solid #f1f5f9;
  &:last-child { border-bottom: none; }
`;

const InfoKey = styled.div`
  font-size: 15px;
  color: #3c3c43;
`;

const InfoVal = styled.div`
  font-size: 15px;
  font-weight: 500;
  color: #000;
  text-align: right;
`;

// ─── Chart card ───────────────────────────────────────────────────────────────

const ChartCard = styled(Card)``;

const StatRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f2f2f7;
`;

const StatBlock = styled.div`
  flex: 1;
`;

const StatNum = styled.div<{ $amber?: boolean }>`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.04em;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: ${p => (p.$amber ? '#e8a33b' : '#1d1d1f')};
`;

const StatLbl = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-top: 4px;
`;

const DeltaBadge = styled.span<{ $pos: boolean }>`
  align-self: center;
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: ${p => (p.$pos ? '#d1fae5' : '#fee2e2')};
  color: ${p => (p.$pos ? '#065f46' : '#991b1b')};
  white-space: nowrap;
  flex-shrink: 0;
`;

const ChartScroll = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  overflow-x: auto;
  padding: 14px 16px 6px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const ChartCol = styled.div<{ $click?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 54px;
  cursor: ${p => (p.$click ? 'pointer' : 'default')};
`;

const Bars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 150px;
  width: 100%;
  justify-content: center;
`;

const BarWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 2px;
  flex: 1;
`;

const BarVal = styled.span<{ $k: 'result' | 'meta' | 'proj' }>`
  font-size: 8px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  line-height: 1;
  color: ${p =>
    p.$k === 'result' ? 'var(--accent, #2563eb)' :
    p.$k === 'proj'   ? '#b45309' : '#6b7280'};
`;

const Bar = styled.div<{ $h: number; $k: 'result' | 'meta' | 'proj' }>`
  width: 100%;
  border-radius: 4px 4px 0 0;
  flex-shrink: 0;
  height: ${p => (p.$h > 0 ? Math.max(p.$h, 4) : 0)}px;
  ${p =>
    p.$k === 'result'
      ? `background: linear-gradient(180deg, color-mix(in srgb, #2563eb 65%, white) 0%, #2563eb 100%);`
      : p.$k === 'meta'
      ? `background: repeating-linear-gradient(135deg, rgba(107,114,128,0.18) 0 5px, transparent 5px 10px); border: 1px solid #d1d5db;`
      : `background: repeating-linear-gradient(135deg, #F5C44C 0 8px, #E8A33B 8px 16px);`}
`;

const NoMetaSlot = styled.div`
  width: 100%;
  height: 26px;
  border: 1.5px dashed #e5e7eb;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  font-weight: 700;
  color: #9ca3af;
`;

const ColLabel = styled.div`
  margin-top: 5px;
  text-align: center;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0.01em;
  line-height: 1.3;
`;

const ColStatus = styled.span<{ $ok: boolean | null }>`
  display: block;
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${p => (p.$ok === true ? '#1FA060' : p.$ok === false ? '#D54343' : '#9ca3af')};
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 8px 16px 12px;
`;

const LegItem = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
`;

const LSwatch = styled.div<{ $k: 'blue' | 'gray' | 'amber' }>`
  width: 16px;
  height: 10px;
  border-radius: 2px;
  ${p =>
    p.$k === 'blue'
      ? `background: linear-gradient(180deg, #60a5fa 0%, #2563eb 100%);`
      : p.$k === 'gray'
      ? `background: #e5e7eb; background-image: repeating-linear-gradient(-45deg, rgba(156,163,175,.7) 0px, rgba(156,163,175,.7) 4px, transparent 4px, transparent 10px); border: 1px solid #d1d5db;`
      : `background: #fde68a; background-image: repeating-linear-gradient(-45deg, rgba(251,191,36,.75) 0px, rgba(251,191,36,.75) 5px, rgba(253,230,138,.4) 5px, rgba(253,230,138,.4) 11px); border: 1px solid #fcd34d;`}
`;

const SaveRow = styled.div`
  padding: 8px 16px 14px;
  border-top: 1px solid #f2f2f7;
`;

const SaveBtn = styled.button`
  width: 100%;
  padding: 11px;
  background: var(--accent, #2563eb);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  &:active { filter: brightness(0.92); }
  &:disabled { background: #d1d5db; cursor: not-allowed; }
`;

// ─── Popover ──────────────────────────────────────────────────────────────────

const Popover = styled.div`
  position: fixed;
  z-index: 4500;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.2);
  padding: 16px;
  width: 230px;
  animation: ${fadeIn} 0.14s ease;
`;

const PopTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const PopField = styled.div`
  margin-bottom: 10px;
`;

const PopLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const PopInput = styled.input`
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  font-family: inherit;
  &:focus { border-color: var(--accent, #2563eb); }
`;

const PopActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const PopBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.13s;
  ${p =>
    p.$primary
      ? `background: var(--accent,#2563eb); color: white; border: none;`
      : `background: white; color: #374151; border: 1px solid #d1d5db;`}
  &:active { filter: brightness(0.92); }
`;

// ─── Equipe local ─────────────────────────────────────────────────────────────

const RespCard = styled(Card)``;

const RespRow = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 13px 16px;
  cursor: ${p => (p.$clickable ? 'pointer' : 'default')};
  -webkit-tap-highlight-color: transparent;
  &:active { background: ${p => (p.$clickable ? '#f1f5f9' : 'transparent')}; }
`;

const RespInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const RespName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RespMeta = styled.div`
  font-size: 13px;
  color: #8e8e93;
  margin-top: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RespBtns = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
`;

const RespIconBtn = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #f2f2f7;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  &:active { background: #e5e5ea; }
`;

const RespEmptyBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 14px 16px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 15px;
  color: #007aff;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  &:active { background: #f1f5f9; }
`;

const CardDivider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 0 16px;
`;

const MultSectionLbl = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 10px 16px 6px;
`;

const MultRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 2px 16px 14px;
`;

const MultChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: #f1f5f9;
  border-radius: 20px;
  padding: 4px 10px 4px 5px;
  font-size: 13px;
  color: #1c1c1e;
`;

// ─── Notes ────────────────────────────────────────────────────────────────────

const NotesCard = styled(Card)`
  padding: 14px 16px;
`;

const NotesEditable = styled.div`
  min-height: 72px;
  font-size: 15px;
  line-height: 1.65;
  color: #1c1c1e;
  outline: none;
  white-space: pre-wrap;
  word-break: break-word;
  &:empty::before {
    content: attr(data-placeholder);
    color: #c7c7cc;
    pointer-events: none;
  }
`;

// ─── Activity section ─────────────────────────────────────────────────────────

const ActivityCard = styled(Card)``;

const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid #f2f2f7;
  padding: 0 4px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }
`;

const Tab = styled.button<{ $a: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 11px 12px;
  background: none;
  border: none;
  border-bottom: 2px solid ${p => (p.$a ? 'var(--accent, #2563eb)' : 'transparent')};
  margin-bottom: -1px;
  font-size: 14px;
  font-weight: ${p => (p.$a ? 700 : 400)};
  color: ${p => (p.$a ? 'var(--accent, #2563eb)' : '#6c6c70')};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: color 0.13s;
`;

const TabBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: #e2e8f0;
  border-radius: 9px;
  font-size: 10px;
  font-weight: 700;
  color: #334155;
`;

// Feed

const FeedGroup = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #8e8e93;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 12px 16px 5px;
`;

const FeedRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 6px 16px 2px;
`;

const FeedDot = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--accent);
  flex-shrink: 0;
  margin-top: 2px;
`;

const FeedBody = styled.div`
  flex: 1;
  min-width: 0;
  padding-bottom: 10px;
`;

const FeedMeta = styled.div`
  font-size: 13px;
  color: #3c3c43;
  display: flex;
  align-items: baseline;
  gap: 4px;
  flex-wrap: wrap;
`;

const FeedAuthor = styled.span`
  font-weight: 700;
  color: #000;
`;

const FeedTime = styled.span`
  font-size: 11px;
  color: #8e8e93;
  margin-left: auto;
  flex-shrink: 0;
`;

const FeedBubble = styled.div`
  margin-top: 6px;
  background: #f1f5f9;
  border-radius: 12px 12px 12px 4px;
  padding: 9px 12px;
  font-size: 14px;
  color: #1c1c1e;
  line-height: 1.5;
`;

const Empty = styled.div`
  padding: 28px 16px;
  text-align: center;
  font-size: 14px;
  color: #8e8e93;
`;

// Demands list

const DCard = styled.div`
  display: flex;
  align-items: stretch;
  border-bottom: 1px solid #f2f2f7;
  &:last-of-type { border-bottom: none; }
`;

const DStripe = styled.div<{ $s: string }>`
  width: 4px;
  flex-shrink: 0;
  background: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#2563eb';
      case 'PAUSADA': return '#f59e0b';
      case 'CONCLUIDA': return '#10b981';
      case 'CANCELADA': return '#ef4444';
      default: return '#9ca3af';
    }
  }};
`;

const DInfo = styled.div`
  flex: 1;
  padding: 10px 13px;
  min-width: 0;
`;

const DTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DMeta = styled.div`
  font-size: 12px;
  color: #8e8e93;
  margin-top: 3px;
  display: flex;
  gap: 5px;
  align-items: center;
  flex-wrap: wrap;
`;

const StatusPill = styled.span<{ $s: string }>`
  display: inline-flex;
  align-items: center;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  background: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#dbeafe';
      case 'PAUSADA': return '#fef3c7';
      case 'CONCLUIDA': return '#d1fae5';
      case 'CANCELADA': return '#fee2e2';
      default: return '#f2f2f7';
    }
  }};
  color: ${p => {
    switch (p.$s) {
      case 'EM_ANDAMENTO': return '#1e40af';
      case 'PAUSADA': return '#92400e';
      case 'CONCLUIDA': return '#065f46';
      case 'CANCELADA': return '#991b1b';
      default: return '#6b7280';
    }
  }};
`;

const NewDemandBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  padding: 12px 16px;
  background: none;
  border: none;
  border-top: 1px solid #f2f2f7;
  font-size: 14px;
  font-weight: 600;
  color: #007aff;
  cursor: pointer;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  &:active { background: #f2f2f7; }
`;

// Contacts

const CRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #f2f2f7;
  &:last-child { border-bottom: none; }
`;

const CInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #000;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const CCargo = styled.div`
  font-size: 12px;
  color: #8e8e93;
  margin-top: 1px;
`;

// ─── Composer ─────────────────────────────────────────────────────────────────

const ComposerBar = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
  background: rgba(248, 250, 252, 0.96);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const ComposerInput = styled.input`
  flex: 1;
  padding: 9px 14px;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 22px;
  font-size: 15px;
  color: #000;
  outline: none;
  font-family: inherit;
  -webkit-appearance: none;
  transition: border-color 0.13s;
  &:focus { border-color: var(--accent, #2563eb); }
  &::placeholder { color: #c7c7cc; }
`;

const ComposerSend = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--accent, #2563eb);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  transition: filter 0.12s, transform 0.1s;
  -webkit-tap-highlight-color: transparent;
  &:active { transform: scale(0.92); }
  &:disabled { background: #d1d5db; }
`;

// ─── Responsible bottom sheet ─────────────────────────────────────────────────

const SheetBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 4100;
  animation: ${fadeIn} 0.18s ease;
`;

const RespSheet = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 20px 20px 0 0;
  z-index: 4200;
  animation: ${slideUp} 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  max-height: 72vh;
  display: flex;
  flex-direction: column;
`;

const SheetHandle = styled.div`
  width: 36px;
  height: 4px;
  background: #d1d5db;
  border-radius: 2px;
  margin: 10px auto 0;
  flex-shrink: 0;
`;

const SheetTitle = styled.div`
  font-size: 17px;
  font-weight: 600;
  color: #000;
  padding: 14px 20px 8px;
  flex-shrink: 0;
`;

const SheetSearch = styled.input`
  margin: 0 16px 10px;
  padding: 9px 14px;
  background: #f2f2f7;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  color: #000;
  outline: none;
  font-family: inherit;
  flex-shrink: 0;
  &::placeholder { color: #8e8e93; }
`;

const SheetList = styled.div`
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const SheetOpt = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 20px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: #000;
  text-align: left;
  -webkit-tap-highlight-color: transparent;
  border-bottom: 1px solid #f2f2f7;
  &:last-child { border-bottom: none; }
  &:active { background: #f2f2f7; }
`;

// ─── Misc ─────────────────────────────────────────────────────────────────────

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

// ─── Component ────────────────────────────────────────────────────────────────

type ElectionRow = { ano: number; votos: string; meta: string };
type PopState = { ano: number; meta: string; result: string; x: number; y: number };

const CityModalMobile: React.FC<CityModalProps> = ({
  isOpen,
  onClose,
  city,
  teamMembers = [],
  onSave,
  onAssignResponsible,
  onNewDemand,
  onDemandClick,
  demandPanel,
  onAddRegistro,
  registros = [],
  demandas = [],
  contatos = [],
  loadingRegistros = false,
  loadingDemandas = false,
  loadingContatos = false,
}) => {
  const { isAdmin } = useAuth();
  const { can } = usePermissionCheck();
  const canEdit    = can('cities.edit.election_results');
  const canView    = can('cities.view.election_results');
  const canViewComments = can('cities.comments.view');
  const canComment = can('cities.comments.create');

  // chart state
  const [elections, setElections] = useState<ElectionRow[]>([]);
  const [proj, setProj] = useState('');
  const snap = useRef('');

  // notes
  const notesRef = useRef<HTMLDivElement>(null);

  // responsible sheet
  const [showRespSheet, setShowRespSheet] = useState(false);
  const [respQ, setRespQ] = useState('');
  const [assigningResp, setAssigningResp] = useState(false);

  // activity
  const [atab, setAtab] = useState<'comentarios' | 'demandas' | 'contatos'>(
    canViewComments ? 'comentarios' : 'demandas'
  );

  // comment
  const [comment, setComment] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);

  // popover
  const [pop, setPop] = useState<PopState | null>(null);
  const popRef = useRef<HTMLDivElement>(null);

  // contact detail
  const [openContatoId, setOpenContatoId] = useState<number | null>(null);

  // ── body scroll lock ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isOpen]);

  // ── init from city ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!city) return;
    setProj(city.projecao_votos?.toString() ?? '');
    const sorted = [...(city.electionHistory ?? [])].sort((a, b) => a.ano - b.ano);
    const rows: ElectionRow[] = sorted.map(e => ({
      ano: e.ano,
      votos: e.votos_obtidos?.toString() ?? '',
      meta: e.meta_votos?.toString() ?? '',
    }));
    setElections(rows);
    snap.current = JSON.stringify(rows);
    setAtab(canViewComments ? 'comentarios' : 'demandas');
    setPop(null);
    setComment('');
  }, [city]);

  // ── popover outside-click ───────────────────────────────────────────────────
  useEffect(() => {
    if (!pop) return;
    const h = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) savePop();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [pop]);

  if (!city) return null;

  // ── chart math ──────────────────────────────────────────────────────────────
  const lastEl = elections[elections.length - 1];
  const isCurrentCampaign = lastEl && !parseInt(lastEl.votos || '0');
  const pastElections = isCurrentCampaign ? elections.slice(0, -1) : elections;

  const targetYear  = isCurrentCampaign ? lastEl.ano : TARGET_YEAR;
  const targetMeta  = isCurrentCampaign ? (lastEl.meta || proj) : proj;
  const targetMetaN = parseInt(targetMeta || '0');

  const allNums = pastElections
    .flatMap(e => [parseInt(e.votos) || 0, parseInt(e.meta) || 0])
    .concat(targetMetaN ? [targetMetaN] : []);
  const maxN = Math.max(...allNums, 1);

  const toH = (v: string | number | null | undefined) => {
    const n = typeof v === 'string' ? parseInt(v) : Number(v ?? 0);
    if (!n || isNaN(n)) return 0;
    return Math.round((n / maxN) * 140);
  };

  const metasBatidas = pastElections.filter(e => {
    const v = parseInt(e.votos || '0'), m = parseInt(e.meta || '0');
    return m > 0 && v >= m;
  }).length;
  const withMeta = pastElections.filter(e => e.meta).length;

  const lastV   = parseInt(pastElections[pastElections.length - 1]?.votos || '0');
  const lastAno = pastElections[pastElections.length - 1]?.ano;
  const delta   = lastV > 0 && targetMetaN > 0
    ? Math.round(((targetMetaN - lastV) / lastV) * 100) : null;

  const isDirty =
    JSON.stringify(elections) !== snap.current ||
    proj !== (city.projecao_votos?.toString() ?? '');

  // ── popover helpers ─────────────────────────────────────────────────────────
  const openPop = (ano: number, ev: React.MouseEvent) => {
    if (!canEdit) return;
    const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
    const ex = elections.find(x => x.ano === ano);
    const isTgt = ano === targetYear;
    setPop({
      ano,
      meta: isTgt ? targetMeta : (ex?.meta ?? ''),
      result: isTgt ? '' : (ex?.votos ?? ''),
      x: Math.min(rect.left + rect.width / 2, window.innerWidth - 245),
      y: Math.min(rect.bottom + 10, window.innerHeight - 250),
    });
  };

  const savePop = () => {
    if (!pop) return;
    if (pop.ano === targetYear) {
      if (isCurrentCampaign) {
        setElections(prev =>
          prev.map(x => (x.ano === targetYear ? { ...x, meta: pop.meta } : x))
        );
      } else {
        setProj(pop.meta);
      }
    } else {
      setElections(prev => {
        const idx = prev.findIndex(x => x.ano === pop.ano);
        if (idx === -1) return [...prev, { ano: pop.ano, votos: pop.result, meta: pop.meta }];
        return prev.map((x, i) => (i === idx ? { ...x, votos: pop.result, meta: pop.meta } : x));
      });
    }
    setPop(null);
  };

  // ── save / send ─────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const history: CityElectionResult[] = elections.map(e => ({
        ano: e.ano,
        votos_obtidos: parseInt(e.votos) || 0,
        meta_votos: e.meta ? parseInt(e.meta) : null,
      }));
      const origYears = (JSON.parse(snap.current) as ElectionRow[]).map(e => e.ano);
      const deleted = origYears.filter(y => !elections.map(e => e.ano).includes(y));
      await onSave(city.id, {
        electionHistory: history,
        projecao_votos: parseInt(proj) || undefined,
        ...(deleted.length > 0 && { deletedElectionYears: deleted }),
      });
      snap.current = JSON.stringify(elections);
    } finally {
      setSaving(false);
    }
  };

  const sendComment = async () => {
    if (!comment.trim() || !onAddRegistro) return;
    setSending(true);
    try {
      await onAddRegistro(city.id, comment.trim());
      setComment('');
    } finally {
      setSending(false);
    }
  };

  const assignResp = async (uid: number) => {
    if (!onAssignResponsible) return;
    setAssigningResp(true);
    setShowRespSheet(false);
    setRespQ('');
    try { await onAssignResponsible(city.id, uid); }
    finally { setAssigningResp(false); }
  };

  const notesBlur = async () => {
    const text = notesRef.current?.innerText ?? '';
    if (text !== (city.notas_estrategicas ?? '')) {
      await onSave(city.id, { notas_estrategicas: text });
    }
  };

  // ── feed rendering ──────────────────────────────────────────────────────────
  const renderFeed = () => {
    if (registros.length === 0) return <Empty>Nenhum comentário ainda</Empty>;
    const today = new Date();
    const toDs = (d: Date) => d.toISOString().slice(0, 10);
    const todayDs = toDs(today);
    const yest = new Date(today); yest.setDate(today.getDate() - 1);
    const yestDs = toDs(yest);

    let lastGroup = '';
    const els: React.ReactNode[] = [];

    registros.forEach(r => {
      const autor  = (r as any).autor ?? (r as any).usuario;
      const texto  = (r as any).texto ?? (r as any).descricao_atualizacao ?? '';
      const datStr = (r as any).data ?? (r as any).created_at;
      const d  = new Date(datStr);
      const ds = toDs(d);

      let label =
        ds === todayDs ? `HOJE · ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).toUpperCase()}` :
        ds === yestDs  ? `ONTEM · ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).toUpperCase()}` :
        d.toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' }).toUpperCase();

      if (label !== lastGroup) {
        els.push(<FeedGroup key={`g-${ds}-${r.id}`}>{label}</FeedGroup>);
        lastGroup = label;
      }

      const timeStr = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
      els.push(
        <FeedRow key={r.id}>
          <FeedDot>
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
          </FeedDot>
          <FeedBody>
            <FeedMeta>
              <FeedAuthor>{autor?.nome ?? 'Sistema'}</FeedAuthor>
              <span style={{ color: '#8e8e93' }}>comentou</span>
              <FeedTime>{timeStr}</FeedTime>
            </FeedMeta>
            <FeedBubble>{texto}</FeedBubble>
          </FeedBody>
        </FeedRow>
      );
    });
    return els;
  };

  const ibge = city.ibge_id?.toString() ?? '—';
  const showComposer = atab === 'comentarios' && canViewComments;

  return (
    <>
      {/* ── Main overlay ───────────────────────────────────────────────────── */}
      <Overlay $open={isOpen} onClick={onClose}>
        <Sheet $open={isOpen} onClick={e => e.stopPropagation()}>

          {/* Nav */}
          <Nav>
            <NavIconBtn onClick={onClose} aria-label="Fechar">
              <svg width="10" height="16" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 10 16">
                <path d="M8 1L1 8 8 15" />
              </svg>
            </NavIconBtn>
            <NavCenter>
              <NavSup>Cidade</NavSup>
              <NavTitle>{city.nome}</NavTitle>
            </NavCenter>
            <NavIconBtn aria-label="Mais opções">
              <svg width="4" height="16" fill="currentColor" viewBox="0 0 4 16">
                <circle cx="2" cy="2"  r="1.5" />
                <circle cx="2" cy="8"  r="1.5" />
                <circle cx="2" cy="14" r="1.5" />
              </svg>
            </NavIconBtn>
          </Nav>

          {/* Body */}
          <Body>

            {/* ── Hero ──────────────────────────────────────────────────────── */}
            <Hero>
              <HeroName>{city.nome}</HeroName>
              <HeroSub>
                {[
                  city.regiao          && `Reg. ${city.regiao}`,
                  city.populacao       && `${fmtN(city.populacao)} hab.`,
                  city.populacao_votantes && `${fmtN(city.populacao_votantes)} eleitores`,
                ].filter(Boolean).join(' · ')}
              </HeroSub>
            </Hero>

            {/* ── Histórico de votos ────────────────────────────────────────── */}
            {canView && (
              <>
                <SectionHead>
                  <SectionLabel>Histórico de votos</SectionLabel>
                  {canEdit && (
                    <SectionEditBtn>
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                      toque para editar
                    </SectionEditBtn>
                  )}
                </SectionHead>

                <ChartCard>
                  {/* Stat row */}
                  <StatRow>
                    <StatBlock>
                      <StatNum $amber>{targetMetaN ? fmtN(targetMetaN) : '—'}</StatNum>
                      <StatLbl>Meta {targetYear}</StatLbl>
                    </StatBlock>
                    <StatBlock>
                      <StatNum>{metasBatidas}/{withMeta}</StatNum>
                      <StatLbl>Metas batidas</StatLbl>
                    </StatBlock>
                    {delta !== null && (
                      <DeltaBadge $pos={delta >= 0}>
                        {delta >= 0 ? '+' : ''}{delta}% vs '{String(lastAno ?? targetYear - 4).slice(2)}
                      </DeltaBadge>
                    )}
                  </StatRow>

                  {/* Bar chart */}
                  <ChartScroll>
                    {pastElections.map(el => {
                      const r = parseInt(el.votos || '0');
                      const m = parseInt(el.meta  || '0');
                      const bateu = m > 0 && r >= m;
                      return (
                        <ChartCol key={el.ano} $click={canEdit} onClick={e => openPop(el.ano, e)}>
                          <Bars>
                            {el.meta ? (
                              <BarWrap>
                                <BarVal $k="meta">{fmtN(m)}</BarVal>
                                <Bar $h={toH(el.meta)} $k="meta" />
                              </BarWrap>
                            ) : (
                              <BarWrap><NoMetaSlot>+meta</NoMetaSlot></BarWrap>
                            )}
                            <BarWrap>
                              <BarVal $k="result">{r > 0 ? fmtN(r) : ''}</BarVal>
                              <Bar $h={toH(el.votos)} $k="result" />
                            </BarWrap>
                          </Bars>
                          <ColLabel>
                            '{String(el.ano).slice(2)}
                            <ColStatus $ok={m > 0 ? bateu : null}>
                              {m > 0 ? (bateu ? '✓' : '✗') : '–'}
                            </ColStatus>
                          </ColLabel>
                        </ChartCol>
                      );
                    })}
                    {/* Target / projection column */}
                    <ChartCol $click={canEdit} onClick={e => openPop(targetYear, e)}>
                      <Bars>
                        <BarWrap>
                          <BarVal $k="proj">{targetMetaN > 0 ? fmtN(targetMetaN) : ''}</BarVal>
                          <Bar $h={toH(targetMeta)} $k="proj" />
                        </BarWrap>
                      </Bars>
                      <ColLabel>
                        '{String(targetYear).slice(2)}
                        <ColStatus $ok={null}>META</ColStatus>
                      </ColLabel>
                    </ChartCol>
                  </ChartScroll>

                  {/* Legend */}
                  <Legend>
                    <LegItem><LSwatch $k="blue" />Realizado</LegItem>
                    <LegItem><LSwatch $k="gray" />Meta</LegItem>
                    <LegItem><LSwatch $k="amber" />Meta {targetYear}</LegItem>
                  </Legend>

                  {/* Save */}
                  {canEdit && isDirty && (
                    <SaveRow>
                      <SaveBtn onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner /> : 'Salvar alterações'}
                      </SaveBtn>
                    </SaveRow>
                  )}
                </ChartCard>
              </>
            )}

            {/* ── Sobre a cidade ────────────────────────────────────────────── */}
            <SectionHead>
              <SectionLabel>Sobre a cidade</SectionLabel>
            </SectionHead>
            <Card>
              <InfoRow><InfoKey>UF</InfoKey><InfoVal style={{ fontWeight: 700 }}>{city.uf}</InfoVal></InfoRow>
              {city.regiao     && <InfoRow><InfoKey>Região</InfoKey><InfoVal>{city.regiao}</InfoVal></InfoRow>}
              {city.mesorregiao && <InfoRow><InfoKey>Mesorregião</InfoKey><InfoVal>{city.mesorregiao}</InfoVal></InfoRow>}
              <InfoRow>
                <InfoKey>Habitantes</InfoKey>
                <InfoVal style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtN(city.populacao)}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Eleitores aptos</InfoKey>
                <InfoVal style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtN(city.populacao_votantes)}</InfoVal>
              </InfoRow>
              <InfoRow>
                <InfoKey>Código IBGE</InfoKey>
                <InfoVal style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, color: '#6b7280', fontWeight: 400 }}>
                  {ibge}
                </InfoVal>
              </InfoRow>
            </Card>

            {/* ── Equipe local ──────────────────────────────────────────────── */}
            <SectionHead>
              <SectionLabel>Equipe local</SectionLabel>
              {isAdmin && (
                <SectionEditBtn onClick={() => setShowRespSheet(true)}>
                  <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </SectionEditBtn>
              )}
            </SectionHead>
            <RespCard>
              {city.responsavel ? (
                <RespRow $clickable={isAdmin} onClick={isAdmin ? () => setShowRespSheet(true) : undefined}>
                  <UserAvatar name={city.responsavel.nome} fotoUrl={city.responsavel.foto_url} size={44} />
                  <RespInfo>
                    <RespName>
                      {assigningResp ? <Spinner /> : city.responsavel.nome}
                    </RespName>
                    <RespMeta>
                      Responsável
                      {city.ultima_visita && ` · última visita ${fmtD(city.ultima_visita)}`}
                    </RespMeta>
                  </RespInfo>
                  <RespBtns onClick={e => e.stopPropagation()}>
                    <RespIconBtn
                      href={`https://wa.me/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="WhatsApp"
                      style={{ color: '#25d366' }}
                    >
                      <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 2.025.507 3.934 1.395 5.608L0 24l6.561-1.37A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.865 0-3.607-.484-5.12-1.332l-.367-.217-3.894.813.826-3.796-.238-.389A9.952 9.952 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/>
                      </svg>
                    </RespIconBtn>
                    <RespIconBtn as="button" title="Ligar" style={{ color: '#007aff', background: '#f2f2f7', border: 'none', cursor: 'pointer' }}>
                      <svg width="17" height="17" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </RespIconBtn>
                  </RespBtns>
                </RespRow>
              ) : (
                <RespEmptyBtn onClick={isAdmin ? () => setShowRespSheet(true) : undefined}>
                  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M12 4v16m8-8H4" strokeLinecap="round" />
                  </svg>
                  {isAdmin ? 'Atribuir responsável' : 'Sem responsável atribuído'}
                </RespEmptyBtn>
              )}

              <CardDivider />
              <MultSectionLbl>Multiplicadores</MultSectionLbl>
              <MultRow>
                {(city.multiplicadores ?? []).length > 0
                  ? (city.multiplicadores ?? []).map(m => (
                    <MultChip key={m.id}>
                      <UserAvatar name={m.nome} fotoUrl={m.foto_url} size={20} />
                      {m.nome}
                    </MultChip>
                  ))
                  : <span style={{ fontSize: 14, color: '#8e8e93' }}>Nenhum cadastrado</span>
                }
              </MultRow>
            </RespCard>

            {/* ── Notas estratégicas ────────────────────────────────────────── */}
            <SectionHead>
              <SectionLabel>Notas estratégicas</SectionLabel>
              <SectionEditBtn onClick={() => notesRef.current?.focus()}>
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </SectionEditBtn>
            </SectionHead>
            <NotesCard>
              <NotesEditable
                ref={notesRef}
                contentEditable
                suppressContentEditableWarning
                data-placeholder="Quem manda na cidade? Quais são as portas de entrada? Eventos-chave?"
                onBlur={notesBlur}
                onKeyDown={e => { if (e.key === 'Escape') (e.target as HTMLElement).blur(); }}
                dangerouslySetInnerHTML={{ __html: city.notas_estrategicas ?? '' }}
              />
            </NotesCard>

            {/* ── Atividade ─────────────────────────────────────────────────── */}
            <SectionHead>
              <SectionLabel>Atividade</SectionLabel>
            </SectionHead>
            <ActivityCard>
              <TabBar>
                {canViewComments && (
                  <Tab $a={atab === 'comentarios'} onClick={() => setAtab('comentarios')}>
                    Comentários
                    {registros.length > 0 && <TabBadge>{registros.length}</TabBadge>}
                  </Tab>
                )}
                <Tab $a={atab === 'demandas'} onClick={() => setAtab('demandas')}>
                  Demandas
                  {demandas.length > 0 && <TabBadge>{demandas.length}</TabBadge>}
                </Tab>
                <Tab $a={atab === 'contatos'} onClick={() => setAtab('contatos')}>
                  Contatos
                  {contatos.length > 0 && <TabBadge>{contatos.length}</TabBadge>}
                </Tab>
              </TabBar>

              {/* Comentários */}
              {atab === 'comentarios' && canViewComments && (
                <div style={{ paddingBottom: 6 }}>
                  {loadingRegistros
                    ? [1,2,3].map(i => (
                        <MShimmerRow key={i}>
                          <MShimmerCircle />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <MShimmerBar $w="60%" />
                            <MShimmerBar $w="85%" $h="11px" />
                          </div>
                        </MShimmerRow>
                      ))
                    : renderFeed()}
                </div>
              )}

              {/* Demandas */}
              {atab === 'demandas' && (
                <>
                  {loadingDemandas
                    ? [1,2,3,4].map(i => (
                        <MShimmerRow key={i}>
                          <MShimmerBar $w="4px" $h="40px" style={{ borderRadius: 2, flexShrink: 0 } as any} />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <MShimmerBar $w="70%" />
                            <MShimmerBar $w="45%" $h="11px" />
                          </div>
                        </MShimmerRow>
                      ))
                    : demandas.length === 0
                    ? <Empty>Nenhuma demanda vinculada</Empty>
                    : demandas.map(d => (
                      <DCard key={d.id} onClick={() => onDemandClick?.(d)} style={onDemandClick ? { cursor: 'pointer' } : undefined}>
                        <DStripe $s={d.status} />
                        <DInfo>
                          <DTitle>{d.titulo}</DTitle>
                          <DMeta>
                            <StatusPill $s={d.status}>{STATUS_LABEL[d.status] ?? d.status}</StatusPill>
                            <span>#{d.id}</span>
                            {(d as any).solicitante?.nome && <span>{(d as any).solicitante.nome}</span>}
                            <span>{ago(d.data_cadastro)}</span>
                          </DMeta>
                        </DInfo>
                      </DCard>
                    ))
                  }
                  {onNewDemand && (
                    <NewDemandBtn onClick={() => onNewDemand(city.id)}>
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" strokeLinecap="round">
                        <path d="M12 4v16m8-8H4" />
                      </svg>
                      Nova demanda para esta cidade
                    </NewDemandBtn>
                  )}
                </>
              )}

              {/* Contatos */}
              {atab === 'contatos' && (
                <>
                  {loadingContatos
                    ? [1,2,3,4,5].map(i => (
                        <MShimmerRow key={i}>
                          <MShimmerCircle />
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <MShimmerBar $w="55%" />
                            <MShimmerBar $w="35%" $h="11px" />
                          </div>
                        </MShimmerRow>
                      ))
                    : contatos.length === 0
                    ? <Empty>Nenhum contato cadastrado</Empty>
                    : contatos.map(c => (
                      <CRow key={c.id} onClick={() => setOpenContatoId(c.id)} style={{ cursor: 'pointer' }}>
                        <UserAvatar name={c.nome} fotoUrl={c.foto_url} size={36} />
                        <CInfo>
                          <CName>
                            {c.nome}
                            {c.is_multiplicador && <span style={{ color: '#f59e0b', fontSize: 12 }}>★</span>}
                          </CName>
                          {c.cargo && <CCargo>{c.cargo}</CCargo>}
                        </CInfo>
                      </CRow>
                    ))
                  }
                </>
              )}
            </ActivityCard>

            {/* Bottom spacer */}
            <div style={{ height: 20 }} />
          </Body>

          {/* ── Composer ──────────────────────────────────────────────────────── */}
          {showComposer && (
            <ComposerBar>
              <ComposerInput
                value={comment}
                onChange={e => setComment(e.target.value)}
                placeholder="Anote uma conversa ou observação..."
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); sendComment(); } }}
              />
              <ComposerSend
                onClick={sendComment}
                disabled={!comment.trim() || sending || !canComment || !onAddRegistro}
                aria-label="Enviar"
              >
                {sending ? <Spinner /> : (
                  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                )}
              </ComposerSend>
            </ComposerBar>
          )}

        </Sheet>
      </Overlay>

      {/* ── Responsible sheet ──────────────────────────────────────────────────── */}
      {showRespSheet && (
        <>
          <SheetBackdrop onClick={() => { setShowRespSheet(false); setRespQ(''); }} />
          <RespSheet onClick={e => e.stopPropagation()}>
            <SheetHandle />
            <SheetTitle>Atribuir responsável</SheetTitle>
            <SheetSearch
              autoFocus
              placeholder="Buscar membro da equipe..."
              value={respQ}
              onChange={e => setRespQ(e.target.value)}
            />
            <SheetList>
              {teamMembers
                .filter(m => m.nome.toLowerCase().includes(respQ.toLowerCase()))
                .map(m => (
                  <SheetOpt key={m.id} onClick={() => assignResp(m.id)}>
                    <UserAvatar name={m.nome} fotoUrl={m.foto_url} size={36} />
                    <div>
                      <div style={{ fontWeight: 600 }}>{m.nome}</div>
                      <div style={{ fontSize: 13, color: '#8e8e93' }}>
                        {m.tipo_usuario === 'ADMIN' ? 'Administrador' : `Assessor · ${m.nivel_acesso}`}
                      </div>
                    </div>
                  </SheetOpt>
                ))}
            </SheetList>
          </RespSheet>
        </>
      )}

      {/* ── Chart edit popover ─────────────────────────────────────────────────── */}
      {pop && canEdit && (
        <Popover ref={popRef} style={{ left: pop.x, top: pop.y }}>
          <PopTitle>
            {pop.ano === targetYear ? `Meta ${pop.ano}` : `Eleição ${pop.ano}`}
          </PopTitle>
          {pop.ano !== targetYear && (
            <PopField>
              <PopLabel>Votos obtidos</PopLabel>
              <PopInput
                type="number"
                min="0"
                autoFocus
                value={pop.result}
                onChange={e => setPop(p => p ? { ...p, result: e.target.value } : p)}
                onKeyDown={e => { if (e.key === 'Enter') savePop(); if (e.key === 'Escape') setPop(null); }}
              />
            </PopField>
          )}
          <PopField>
            <PopLabel>Meta de votos</PopLabel>
            <PopInput
              type="number"
              min="0"
              autoFocus={pop.ano === targetYear}
              value={pop.meta}
              onChange={e => setPop(p => p ? { ...p, meta: e.target.value } : p)}
              onKeyDown={e => { if (e.key === 'Enter') savePop(); if (e.key === 'Escape') setPop(null); }}
            />
          </PopField>
          <PopActions>
            <PopBtn onClick={() => setPop(null)}>Cancelar</PopBtn>
            <PopBtn $primary onClick={savePop}>OK</PopBtn>
          </PopActions>
        </Popover>
      )}
      {/* ── Demand detail modal (mobile variant auto-renders via DemandDetailModal) ── */}
      <MobileOnly>
      {demandPanel && (
        <DemandDetailModal
          isOpen={!!demandPanel.isOpen}
          demand={demandPanel.demand as any}
          onClose={demandPanel.onClose}
          onUpdate={() => {
            if (demandPanel.demand?.id && demandPanel.onDemandUpdated) {
              demandPanel.onDemandUpdated(demandPanel.demand.id);
            }
          }}
          activeTab="registros"
          onTabChange={() => {}}
          registros={demandPanel.registros ?? []}
          loadingRegistros={demandPanel.loadingRegistros ?? false}
          newRegistro={demandPanel.newRegistro ?? ''}
          onNewRegistroChange={demandPanel.onNewRegistroChange ?? (() => {})}
          onAddRegistro={demandPanel.onAddRegistro}
          addingRegistro={demandPanel.addingRegistro ?? false}
          editingRegistro={demandPanel.editingRegistro ?? null}
          editingText={demandPanel.editingText ?? ''}
          onEditRegistro={demandPanel.onEditRegistro ?? (() => {})}
          onSaveEdit={demandPanel.onSaveEdit ?? (() => {})}
          onCancelEdit={demandPanel.onCancelEdit ?? (() => {})}
          onEditTextChange={demandPanel.onEditTextChange ?? (() => {})}
          onDeleteRegistro={demandPanel.onDeleteRegistro ?? (() => {})}
          savingRegistroId={demandPanel.savingRegistroId ?? null}
          deletingRegistroId={demandPanel.deletingRegistroId ?? null}
          openMenuId={demandPanel.openMenuId ?? null}
          onMenuToggle={demandPanel.onMenuToggle ?? (() => {})}
          canViewUpdates={demandPanel.canViewUpdates ?? false}
          canCreateUpdates={demandPanel.canCreateUpdates ?? false}
          canEditUpdate={demandPanel.canEditUpdate ?? (() => false)}
          canDeleteUpdate={demandPanel.canDeleteUpdate ?? (() => false)}
          canEditDemand={demandPanel.canEditDemand ?? false}
          canDeleteDemand={demandPanel.canDeleteDemand ?? false}
          canAssignResponsible={demandPanel.canAssignResponsible ?? false}
          canUnassignResponsible={demandPanel.canUnassignResponsible ?? false}
          historico={demandPanel.historico ?? []}
          loadingHistorico={demandPanel.loadingHistorico ?? false}
        />
      )}
      </MobileOnly>
      <MobileOnly>
        <ContactDetailModalMobile
          isOpen={!!openContatoId}
          contatoId={openContatoId}
          onClose={() => setOpenContatoId(null)}
        />
      </MobileOnly>
    </>
  );
};

export default CityModalMobile;
